import React, { useContext, useState } from 'react';
import * as Sentry from '@sentry/react';
import { flushSync } from 'react-dom';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PrinterIcon from '../resources/assets/icons/PrinterIcon';
import WebViewCommunicatorResource from '../services/resources/WebViewCommunicatorResource';
import PrintOrderManager from '../services/api/PrintOrderManager';
import usePrinters from './usePrinters';
import captureReceipt, { waitForReceiptElement } from '../services/print/ReceiptCapture';
import { InnerPrinterReceiptElementID, PrintContext, ReceiptType } from '../providers/PrintProvider';

/**
 * There is one receipt slot in the whole app — PrintProvider's `data` — and a capture holds the
 * node it renders for a few hundred ms on a terminal. Two prints in flight at once means the
 * first to finish clears the slot out from under the second, detaching the node its capture is
 * still holding, so prints that go through the DOM run one at a time. Module scope rather than
 * the provider on purpose: every usePrinter() caller shares the same slot, so they have to
 * share the same queue.
 */
let printQueue = Promise.resolve();

function enqueuePrint(task) {
  const result = printQueue.then(task);
  // The queue itself must never reject, or every print behind a failed one would be skipped.
  printQueue = result.catch(() => {});

  return result;
}

export default function usePrinter() {
  const { t } = useTranslation(null, { keyPrefix: 'Hooks:usePrinter' });

  const company = useSelector((state) => state?.company?.company);
  const { print_copies } = company;
  const innerPrinterConnected = useSelector((state) => state.print.innerPrinterConnected);
  const { printsExternally } = usePrinters();
  const { setData } = useContext(PrintContext);
  const [printing, setPrinting] = useState(false);

  function throwPrintingToast(data) {
    const { type, payload } = data;

    switch (type) {
      case ReceiptType.Order:
        return toast(
          <div className="flex">
            <PrinterIcon color="#1C1D28" className="printer-icon mr-2" />
            <span>
              <Trans
                t={t}
                values={{
                  count: parseInt(print_copies, 10),
                  orderNumber: payload?.number,
                }}
              >
                toasts.orders.print
              </Trans>
            </span>
          </div>,
        );
      default:
        return toast(
          <div className="flex">
            <PrinterIcon color="#1C1D28" className="printer-icon mr-2" />
            <span>{t('toasts.printing')}</span>
          </div>,
        );
    }
  }

  async function print(data, printOnTerminalOnly = false) {
    if (printOnTerminalOnly && !innerPrinterConnected && !printsExternally) {
      setPrinting(true);
      await PrintOrderManager.print(data.payload.id);
      setPrinting(false);

      return;
    }

    setPrinting(true);
    throwPrintingToast(data);

    // Everything that touches the receipt slot stays inside the queued task, cleanup included,
    // so the next print cannot claim the slot until this one has let go of it.
    return enqueuePrint(async () => {
      // Flushed, not scheduled: the poll below runs on the very next line, and React would
      // otherwise still be showing the previous print's receipt for it to capture.
      flushSync(() => setData(data));

      try {
        const element = await waitForReceiptElement(InnerPrinterReceiptElementID);
        const receipt = await captureReceipt(element);

        switch (true) {
          case printsExternally:
            WebViewCommunicatorResource.printOrderExternal({
              uri: receipt,
              copies: company.print_copies,
            });
            break;
          default:
            WebViewCommunicatorResource.printOrder({
              uri: receipt,
              copies: company.print_copies,
            });
        }

        printPrepSuccess();
      } catch (error) {
        printPrepError(error);
      }
    });
  }

  function printPrepSuccess() {
    setPrinting(false);
    setData(null);
  }

  function printPrepError(error) {
    // Sentry only sees what is thrown, and this path is now caught — report it, so an
    // unprintable receipt stays visible instead of being just a toast on a terminal.
    Sentry.captureException(error);
    setPrinting(false);
    setData(null);
    toast(t('toasts.print_failed'));
  }

  return {
    print,
    printing,
    setPrinting,
  };
}
